import { motion } from "framer-motion";

// Body diagram with muscle group highlighting
const BodyDiagram = ({ activeMusles = [] }) => {
  // Normalize muscle names
  const normalizedMuscles = activeMusles.map(m => m.toLowerCase().replace('_', ''));
  
  const isActive = (muscle) => {
    const muscleAliases = {
      chest: ['chest', 'pecs', 'pectoral'],
      shoulders: ['shoulders', 'delts', 'deltoids'],
      biceps: ['biceps', 'arms'],
      triceps: ['triceps', 'arms'],
      forearms: ['forearms', 'arms'],
      abs: ['abs', 'core', 'abdominals', 'sixpack'],
      obliques: ['obliques', 'core'],
      quads: ['quads', 'quadriceps', 'legs', 'thighs'],
      hamstrings: ['hamstrings', 'legs'],
      glutes: ['glutes', 'butt', 'gluteus'],
      calves: ['calves', 'legs'],
      back: ['back', 'lats', 'latissimus'],
      traps: ['traps', 'trapezius', 'back'],
      fullbody: ['fullbody', 'full_body', 'full body', 'cardio', 'total']
    };
    
    for (const [group, aliases] of Object.entries(muscleAliases)) {
      if (group === muscle) {
        return aliases.some(alias => 
          normalizedMuscles.some(m => m.includes(alias) || alias.includes(m))
        );
      }
    }
    return normalizedMuscles.includes(muscle);
  };
  
  const isFullBody = normalizedMuscles.some(m => 
    m.includes('full') || m.includes('cardio') || m.includes('total')
  );

  // Animation for active muscles
  const pulseAnimation = {
    animate: {
      opacity: [0.7, 1, 0.7],
      filter: ["drop-shadow(0 0 8px #00FF94)", "drop-shadow(0 0 20px #00FF94)", "drop-shadow(0 0 8px #00FF94)"]
    },
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const activeColor = "#00FF94";
  const inactiveColor = "#333";
  const glowFilter = "drop-shadow(0 0 10px #00FF94)";

  return (
    <div className="relative flex items-center justify-center gap-4">
      {/* Front View */}
      <div className="relative">
        <svg 
          viewBox="0 0 200 400" 
          className="w-32 h-64 md:w-40 md:h-80"
          style={{ filter: isFullBody ? glowFilter : 'none' }}
        >
          {/* Head */}
          <motion.ellipse
            cx="100" cy="30" rx="25" ry="28"
            fill={isFullBody ? activeColor : inactiveColor}
            {...(isFullBody ? pulseAnimation : {})}
          />
          
          {/* Neck */}
          <rect x="90" y="55" width="20" height="15" fill={inactiveColor} />
          
          {/* Shoulders/Traps */}
          <motion.path
            d="M60 70 Q100 65 140 70 L140 85 Q100 80 60 85 Z"
            fill={isActive('shoulders') || isActive('traps') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('shoulders') || isActive('traps') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Chest/Pecs */}
          <motion.path
            d="M65 85 Q100 90 135 85 L130 130 Q100 140 70 130 Z"
            fill={isActive('chest') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('chest') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Abs/Core */}
          <motion.path
            d="M75 130 L125 130 L120 200 Q100 210 80 200 Z"
            fill={isActive('abs') || isActive('core') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('abs') || isActive('core') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Obliques */}
          <motion.path
            d="M65 130 L75 130 L80 195 L65 190 Z"
            fill={isActive('obliques') || isActive('core') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('obliques') || isActive('core') || isFullBody) ? pulseAnimation : {})}
          />
          <motion.path
            d="M135 130 L125 130 L120 195 L135 190 Z"
            fill={isActive('obliques') || isActive('core') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('obliques') || isActive('core') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Left Arm - Bicep/Tricep */}
          <motion.path
            d="M60 85 L45 90 L35 150 L50 155 L60 130 Z"
            fill={isActive('biceps') || isActive('triceps') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('biceps') || isActive('triceps') || isFullBody) ? pulseAnimation : {})}
          />
          {/* Left Forearm */}
          <motion.path
            d="M35 155 L25 210 L40 215 L50 160 Z"
            fill={isActive('forearms') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('forearms') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Right Arm - Bicep/Tricep */}
          <motion.path
            d="M140 85 L155 90 L165 150 L150 155 L140 130 Z"
            fill={isActive('biceps') || isActive('triceps') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('biceps') || isActive('triceps') || isFullBody) ? pulseAnimation : {})}
          />
          {/* Right Forearm */}
          <motion.path
            d="M165 155 L175 210 L160 215 L150 160 Z"
            fill={isActive('forearms') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('forearms') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Left Quad */}
          <motion.path
            d="M75 200 L65 205 L55 300 L75 305 L90 210 Z"
            fill={isActive('quads') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('quads') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Right Quad */}
          <motion.path
            d="M125 200 L135 205 L145 300 L125 305 L110 210 Z"
            fill={isActive('quads') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('quads') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Left Calf */}
          <motion.path
            d="M55 305 L50 370 L70 375 L75 310 Z"
            fill={isActive('calves') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('calves') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Right Calf */}
          <motion.path
            d="M145 305 L150 370 L130 375 L125 310 Z"
            fill={isActive('calves') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('calves') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Glutes hint (visible from front) */}
          <motion.ellipse
            cx="85" cy="205" rx="12" ry="8"
            fill={isActive('glutes') || isFullBody ? activeColor : inactiveColor}
            opacity="0.5"
            {...((isActive('glutes') || isFullBody) ? pulseAnimation : {})}
          />
          <motion.ellipse
            cx="115" cy="205" rx="12" ry="8"
            fill={isActive('glutes') || isFullBody ? activeColor : inactiveColor}
            opacity="0.5"
            {...((isActive('glutes') || isFullBody) ? pulseAnimation : {})}
          />
        </svg>
        <div className="text-center text-xs text-muted-foreground uppercase tracking-wider mt-1">Front</div>
      </div>
      
      {/* Back View */}
      <div className="relative">
        <svg 
          viewBox="0 0 200 400" 
          className="w-32 h-64 md:w-40 md:h-80"
          style={{ filter: isFullBody ? glowFilter : 'none' }}
        >
          {/* Head */}
          <motion.ellipse
            cx="100" cy="30" rx="25" ry="28"
            fill={isFullBody ? activeColor : inactiveColor}
            {...(isFullBody ? pulseAnimation : {})}
          />
          
          {/* Neck */}
          <rect x="90" y="55" width="20" height="15" fill={inactiveColor} />
          
          {/* Traps */}
          <motion.path
            d="M60 70 Q100 60 140 70 L140 95 Q100 85 60 95 Z"
            fill={isActive('traps') || isActive('back') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('traps') || isActive('back') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Upper Back/Lats */}
          <motion.path
            d="M60 95 L65 130 Q100 140 135 130 L140 95 Q100 105 60 95 Z"
            fill={isActive('back') || isActive('lats') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('back') || isActive('lats') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Lower Back */}
          <motion.path
            d="M70 130 L130 130 L125 180 Q100 190 75 180 Z"
            fill={isActive('back') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('back') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Glutes */}
          <motion.path
            d="M70 180 Q100 175 130 180 L135 220 Q100 235 65 220 Z"
            fill={isActive('glutes') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('glutes') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Left Arm - Tricep */}
          <motion.path
            d="M55 95 L40 100 L30 160 L48 165 L55 130 Z"
            fill={isActive('triceps') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('triceps') || isFullBody) ? pulseAnimation : {})}
          />
          {/* Left Forearm */}
          <motion.path
            d="M30 165 L20 215 L38 220 L48 170 Z"
            fill={isActive('forearms') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('forearms') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Right Arm - Tricep */}
          <motion.path
            d="M145 95 L160 100 L170 160 L152 165 L145 130 Z"
            fill={isActive('triceps') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('triceps') || isFullBody) ? pulseAnimation : {})}
          />
          {/* Right Forearm */}
          <motion.path
            d="M170 165 L180 215 L162 220 L152 170 Z"
            fill={isActive('forearms') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('forearms') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Left Hamstring */}
          <motion.path
            d="M70 220 L60 225 L50 310 L75 315 L90 230 Z"
            fill={isActive('hamstrings') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('hamstrings') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Right Hamstring */}
          <motion.path
            d="M130 220 L140 225 L150 310 L125 315 L110 230 Z"
            fill={isActive('hamstrings') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('hamstrings') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Left Calf */}
          <motion.path
            d="M50 315 L45 375 L70 380 L75 320 Z"
            fill={isActive('calves') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('calves') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
          
          {/* Right Calf */}
          <motion.path
            d="M150 315 L155 375 L130 380 L125 320 Z"
            fill={isActive('calves') || isActive('legs') || isFullBody ? activeColor : inactiveColor}
            {...((isActive('calves') || isActive('legs') || isFullBody) ? pulseAnimation : {})}
          />
        </svg>
        <div className="text-center text-xs text-muted-foreground uppercase tracking-wider mt-1">Back</div>
      </div>
    </div>
  );
};

export default BodyDiagram;
